import React from 'react';
import { useAwningModels } from '../../hooks/useAwningModels';

const AwningsProductTiers: React.FC = () => {
  const { modelCards, isLoading } = useAwningModels();

  // Helper function to format currency
  const currency = (amount: number): string => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(amount);
  };



  if (isLoading) {
    return (
      <section id="awnings" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading awning models...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="awnings" className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-primary mb-4">Choose Your Shade Solution</h2>
          <p className="text-xl text-gray-600">From simple shade to premium smart awnings with full customization</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {modelCards.map((model, index) => {
            // Define icons and styling for each model
            const modelConfig = {
              'quick-shade': { icon: '📱', color: 'gray' },
              'smart-shade': { icon: '🏠', color: 'accent' },
              'complete-shade': { icon: '🏆', color: 'primary' },
              'evie-luxe': { icon: '💎', color: 'purple' }
            };

            const config = modelConfig[model.id as keyof typeof modelConfig] || { icon: '🏠', color: 'accent' };
            const isPopular = model.badge === 'Most Popular';

            return (
              <div
                key={model.id}
                className={`bg-white rounded-xl shadow-lg p-8 border-2 hover:shadow-xl transition-shadow flex flex-col h-full relative ${
                  isPopular ? 'border-accent' : 'border-gray-200'
                }`}
              >
                {model.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-accent text-white px-4 py-1 rounded-full text-sm font-semibold">
                    {model.badge}
                  </div>
                )}
                <div className="text-center flex-grow">
                  <div className="bg-accent/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl">{config.icon}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-primary mb-2">{model.name}</h3>
                  <p className="text-gray-600 mb-4">{model.description}</p>
                  <div className="text-4xl font-bold text-accent mb-1">{currency(model.price + model.installCost)} installed</div>
                  <p className="text-sm text-gray-500 mb-4">Awning only {currency(model.price)} + {currency(model.installCost)} install</p>

                  <div className="space-y-3 mb-6 text-left">
                    {model.bullets.map((bullet, bulletIndex) => (
                      <div key={bulletIndex} className="flex items-center">
                        <span className="text-accent mr-3">✓</span>
                        <span>{bullet}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-auto">
                  <div className="text-sm text-gray-600 mb-4">
                    <strong>Best for:</strong> {model.bestFor}
                  </div>
                  <a
                    href="/awning-quote"
                    className="w-full bg-accent text-white px-6 py-3 rounded-lg text-center font-semibold hover:bg-green-700 transition-colors block"
                  >
                    ⚡ Get Instant Quote
                  </a>
                </div>
              </div>
            );
          })}
        </div>


      </div>
    </section>
  );
};

export default AwningsProductTiers;
